import React from "react";

import { useSnackbar } from "notistack";

import { Alert } from "src/components/ui/alert";
import handleApiError, {
  formatErrorDetail,
} from "src/experiences/common/utils/handleApiError";

interface UseApiError {
  somethingWentWrong: (err: any) => void;
  error: string;
  errorType: string | undefined;
  alert: React.ReactNode;
  clearErrors: () => void;
  handleApiError: (res: any) => void;
  setError: (message: string, type?: string) => void;
}

const useApiError = (): UseApiError => {
  const [error, setError] = React.useState<string>("");
  const [errorType, setErrorType] = React.useState<string | undefined>(
    undefined
  );
  const { enqueueSnackbar } = useSnackbar();

  const handleApiErrorCallback = React.useCallback((res: any): void => {
    handleApiError(res, (msg, type) => {
      setError(msg);
      setErrorType(type);
    });
  }, []);

  const somethingWentWrong = React.useCallback(
    (err: any): void => {
      if (err?.status === 403) {
        enqueueSnackbar("You do not have permission to perform this action", {
          variant: "error",
        });
      } else if (
        (err?.status === 400 || err?.status === 422) &&
        err?.body?.detail
      ) {
        enqueueSnackbar(
          formatErrorDetail(err.body.detail) || "Something went wrong.",
          { variant: "error" }
        );
      } else {
        enqueueSnackbar("Something went wrong.", { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const clearErrors = React.useCallback((): void => {
    setError("");
    setErrorType(undefined);
  }, []);

  const alert = error ? (
    <div className="mb-4">
      <Alert variant="destructive">{error}</Alert>
    </div>
  ) : null;

  const setErrorMessage = React.useCallback(
    (message: string, type?: string): void => {
      setError(message);
      setErrorType(type);
    },
    []
  );

  return {
    somethingWentWrong,
    error,
    errorType,
    alert,
    clearErrors,
    handleApiError: handleApiErrorCallback,
    setError: setErrorMessage,
  };
};

export default useApiError;
